/**
 * Persist near-me metrics for admin dashboards. Also mirrors a JSON line to stdout for log drains.
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database, Json } from "@/lib/database.types";
import { recordToJson } from "@/lib/jsonUtils";

export const NEAR_ME_METRIC_ACTION_APPROVAL =
  "new_listing_near_me_approval" as const;
export const NEAR_ME_METRIC_ACTION_DIGEST_BATCH =
  "near_me_digest_batch" as const;
export const NEAR_ME_METRIC_ACTION_DIGEST_EMAIL_CLICK =
  "near_me_digest_email_click" as const;
export const NEAR_ME_METRIC_ACTION_REALTIME_BATCH =
  "near_me_realtime_batch" as const;
export const NEAR_ME_METRIC_ACTION_MATCH_BATCH = "near_me_match_batch" as const;
export const NEAR_ME_METRIC_ACTION_ADMIN_RESEND =
  "new_listing_near_me_admin_resend" as const;
export const NEAR_ME_METRIC_ACTION_ADMIN_WHATSAPP_MATCHED =
  "near_me_admin_whatsapp_matched" as const;
export const NEAR_ME_METRIC_ACTION_ADMIN_WHATSAPP_EXPAND =
  "near_me_admin_whatsapp_expand" as const;
export const NEAR_ME_METRIC_ACTION_CONFIRMED_AVAILABLE =
  "listing_confirmed_available_near_me" as const;

/** WhatsApp near-me skip / action statuses (also used as near_me_notification_metrics.status). */
export const NEAR_ME_WHATSAPP_METRIC_STATUSES = [
  "skipped_subcategory",
  "skipped_staged",
  "skipped_cap",
  "skipped_daily_soft_cap",
  "auto_staged_wave1",
  "admin_manual",
  "admin_expand",
] as const;

/** Funnel / settings events (same string values as former activity-log actions). */
export const NEAR_ME_FUNNEL_ACTIONS = [
  "near_me_entrypoint_impression",
  "near_me_entrypoint_click",
  "near_me_settings_opened",
  "near_me_settings_saved",
  "near_me_prompt_shown",
  "near_me_prompt_dismissed",
  "near_me_prompt_accepted",
  "notification_setup_banner_shown",
  "notification_setup_banner_dismissed",
  "notification_setup_banner_cta_click",
  "notification_setup_sheet_completed",
  "notification_setup_gap_resolved",
  "push_subscription_enabled",
] as const;

export type NearMeFunnelMetricAction = (typeof NEAR_ME_FUNNEL_ACTIONS)[number];

/**
 * `metadata.target` values for `source: "market"` entrypoint events.
 * Filter admin funnel dashboards with `source` + `target` pairs.
 */
export const MARKET_NEAR_ME_ENTRYPOINT_TARGETS = {
  welcomeBanner: "welcome_banner",
  pushReminderBanner: "push_reminder_banner",
  /** Third bullet in welcome banner when platform near-me is enabled */
  alertSettingsLink: "alert_settings_link",
  profileDeeplink: "profile_notifications_deeplink",
  enablePushInline: "enable_push_inline",
} as const;

export type MarketNearMeEntrypointTarget =
  (typeof MARKET_NEAR_ME_ENTRYPOINT_TARGETS)[keyof typeof MARKET_NEAR_ME_ENTRYPOINT_TARGETS];

export function isNearMeFunnelMetricAction(
  value: string,
): value is NearMeFunnelMetricAction {
  return (NEAR_ME_FUNNEL_ACTIONS as readonly string[]).includes(value);
}

function metadataToJson(
  metadata: Record<string, unknown> | null | undefined,
): Json | null {
  if (metadata == null || Object.keys(metadata).length === 0) {
    return null;
  }
  return recordToJson(metadata);
}

export async function insertNearMeNotificationMetric(
  service: SupabaseClient<Database>,
  row: {
    action: string;
    status: string;
    item_id?: string | null;
    error_message?: string | null;
    latency_ms?: number | null;
    metadata?: Record<string, unknown> | null;
  },
): Promise<void> {
  const payload = {
    action: row.action,
    status: row.status,
    item_id: row.item_id ?? null,
    error_message: row.error_message ?? null,
    latency_ms: row.latency_ms ?? null,
    metadata: metadataToJson(row.metadata ?? null),
  };

  console.log(
    JSON.stringify({
      event: "near_me_metric",
      ts: new Date().toISOString(),
      action: payload.action,
      status: payload.status,
      item_id: payload.item_id,
      metadata: row.metadata ?? null,
    }),
  );

  const { error } = await service
    .from("near_me_notification_metrics")
    .insert(payload);

  if (error) {
    console.error("[near_me_notification_metrics] insert:", error);
  }
}
